import PanomNom from '../../third_party/PanomNom';
import getAddress from './getAddress';
import getElevationByCoordinate from './getElevation';
import getPanoramaByCoordinate_ from './getPanoramaByCoordinate';
import { getBackendLayer, getFrontendLayer } from './getElevationLayer';
import { IS_SLOW_DEVICE } from '../../config';
import mapStyles from './mapStyles.json';

let map = null;
let elevator = null;
let geocoder = null;
let streetViewService = null;
let rasterManager = null;

/**
 * Get address by location.
 *
 * @param {location} location, The location we want to measure.
 *
 * @return {Promise<string>} of the address. Can be an empty string.
 */
export const getAddressByLocation = async (location) => getAddress(
    geocoder,
    location,
);

/**
 * Get elevations around location.
 *
 * @param {location} location, The center of the area to search for.
 *
 * @return {number} elevation of the coordinate
 */
export const getElevation = async (location) => getElevationByCoordinate(
    elevator,
    location,
);

/**
 * Get instantiated Elevation Service.
 * Docs: https://developers.google.com/maps/documentation/javascript/elevation
 *
 * @return {ElevationService} instance.
 */
export const getElevator = () => elevator;

/**
 * Get instantiated Geocoding Service.
 * Docs: https://developers.google.com/maps/documentation/javascript/geocoding
 *
 * @return {GeocoderService} instance.
 */
export const getGeocoder = () => geocoder;

/**
 * Get instantiated Street View.
 * Reference: https://developers.google.com/maps/documentation/javascript/reference/street-view-service
 *
 * @return {StreetViewService} instance.
 */
export const getStreetViewService = () => streetViewService;

/**
 * Get instantiated Map.
 * Reference: https://developers.google.com/maps/documentation/javascript/reference/map
 *
 * @return {google.Map} instance.
 */
export const getMap = () => map;

/**
 * Get panorama id by location.
 *
 * @param {coordinate} coordinate Object of lat and lng.
 * @param {zoom} zoom  Zoom value
 *
 * @return {string} panoramaId or empty string.
 */
export const getPanoramaByCoordinate = (
    coordinate,
    zoom,
) => getPanoramaByCoordinate_(
    streetViewService,
    coordinate,
    zoom,
);

/**
 * Get instantiated raster manager.
 *
 * @return {RasterManager} instance.
 */
export const getRasterManager = () => rasterManager;

export const initializeServices = () => {
  const { google } = window;
  elevator = new google.maps.ElevationService();
  geocoder = PanomNom.Utils.getGoogleGeoCoder();
  streetViewService = PanomNom.Utils.getGoogleStreetViewService();
};

/**
 * Initialize Map, StreetView, Geocoder and Elevator.
 *
 * @param {!altitude} altitude, altitude value.
 * @param {!array<Number>} altitudes, altitude step values.
 * @param {!location} cameraDirection, for map.
 * @param {!DomElement} mapElement, element where map is put.
 * @param {!object} mapOptions, for map.
 * @param {!DomElement} panoramaElement, element where streetView is put.
 * @param {!function} updateProgress, update map loading progress.
 *
 * @return {void}.
 */
export default ({
  altitude,
  altitudes,
  cameraDirection,
  mapElement,
  mapOptions,
  panoramaElement,
  updateProgress,
}) => {
  if (map) {
    // Already initialized.
    return;
  }

  const { google } = window;

  map = new google.maps.Map(mapElement, {
    mapTypeId: google.maps.MapTypeId.TERRAIN,
    backgroundColor: 'rgba(48, 48, 48, 1.0)',
    disableDefaultUI: true,
    gestureHandling: 'greedy',
    maxZoom: 15,
    minZoom: 2,
    styles: mapStyles,
    zoomControl: false,
    center: { lat: 0, lng: 0 },
    zoom: 3,

    ...mapOptions,
  });

  if (IS_SLOW_DEVICE) {
    const layer = getBackendLayer(
        altitudes,
        altitude,
        map,
        updateProgress,
    );
    layer.opacity = 0.9;
    rasterManager = layer.rasterManager;
  } else {
    getFrontendLayer(
        altitudes,
        altitude,
        map,
        updateProgress,
    )
        .then((layer) => {
          layer.opacity = 0.9; // eslint-disable-line no-param-reassign
          rasterManager = layer.rasterManager;
          map.overlayMapTypes.setAt(0, layer);
        });
  }
};
